import { gsap } from "../gsap.js";

const MD = 744;

export default function initTimelineHorizontal() {
  const wrappers = document.querySelectorAll(".timeline-horizontal-wrapper");
  if (!wrappers.length) return;

  wrappers.forEach((wrapper) => {
    const nav = wrapper.querySelector(".timeline-horizontal-nav");
    const yearEl = wrapper.querySelector(".timeline-horizontal-year");
    const track = wrapper.querySelector(".timeline-horizontal");
    const items = track
      ? track.querySelectorAll(".timeline-horizontal-item")
      : [];
    const navButtons = nav ? nav.querySelectorAll("ul li button") : [];

    if (!track || items.length === 0) return;

    const total = items.length;
    let currentYear = yearEl ? yearEl.textContent.trim() : "";
    let prevIndex = 0;

    // ── Year digit slide ──
    const digitSlots = [];

    function initYearDisplay() {
      if (!yearEl) return;
      yearEl.innerHTML = "";
      digitSlots.length = 0;
      currentYear.split("").forEach((d) => {
        const slot = document.createElement("span");
        slot.className = "digit-slot";
        const digit = document.createElement("span");
        digit.className = "digit";
        digit.textContent = d;
        slot.appendChild(digit);
        yearEl.appendChild(slot);
        digitSlots.push(digit);
      });
    }

    function animateYear(newYear, scrollDown) {
      if (!yearEl || newYear === currentYear) return;
      const newDigits = newYear.split("");
      const oldDigits = currentYear.split("");
      const slideOut = scrollDown ? "100%" : "-100%";
      const slideIn = scrollDown ? "-100%" : "100%";

      newDigits.forEach((d, i) => {
        if (d === oldDigits[i]) return;
        const digit = digitSlots[i];
        gsap.killTweensOf(digit);
        gsap.to(digit, {
          y: slideOut,
          duration: 0.35,
          ease: "power2.in",
          onComplete: () => {
            digit.textContent = d;
            gsap.set(digit, { y: slideIn });
            gsap.to(digit, {
              y: "0%",
              duration: 0.35,
              ease: "power2.out",
            });
          },
        });
      });
      currentYear = newYear;
    }

    function setActiveNav(year) {
      navButtons.forEach((btn) => {
        const isActive = btn.dataset.year === year;
        btn.classList.toggle("active", isActive);
        if (isActive && nav) {
          const navScroll = nav.querySelector("ul") || nav;
          const btnLeft = btn.offsetLeft;
          const btnWidth = btn.offsetWidth;
          const scrollLeft = navScroll.scrollLeft;
          const visibleWidth = navScroll.clientWidth;
          if (
            btnLeft < scrollLeft ||
            btnLeft + btnWidth > scrollLeft + visibleWidth
          ) {
            navScroll.scrollTo({
              left: btnLeft - visibleWidth / 2 + btnWidth / 2,
              behavior: "smooth",
            });
          }
        }
      });
    }

    initYearDisplay();
    setActiveNav(items[0]?.dataset.year || "");

    const navbar = document.querySelector(".navbar-wrapper");
    let st = null;

    const mm = gsap.matchMedia();

    // ── Mobile (<MD) ──
    mm.add("(max-width: " + (MD - 1) + "px)", () => {
      let lastScrollY = window.scrollY;
      const observer = new IntersectionObserver(
        (entries) => {
          const scrollDown = window.scrollY >= lastScrollY;
          lastScrollY = window.scrollY;
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              animateYear(entry.target.dataset.year || "", scrollDown);
              setActiveNav(entry.target.dataset.year || "");
            }
          });
        },
        { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
      );
      items.forEach((item) => observer.observe(item));

      const clickHandlers = [];
      navButtons.forEach((btn) => {
        const handler = () => {
          const t = track.querySelector(
            '.timeline-horizontal-item[data-year="' + btn.dataset.year + '"]'
          );
          if (t) t.scrollIntoView({ behavior: "smooth", block: "center" });
        };
        btn.addEventListener("click", handler);
        clickHandlers.push({ btn, handler });
      });

      // Track navbar show/hide to update sticky top
      function updateNavTop() {
        if (!nav || !navbar) return;
        const isHidden =
          navbar.classList.contains("scrolled") &&
          navbar.classList.contains("scroll-down");
        nav.style.top = isHidden ? "0px" : navbar.offsetHeight + "px";
      }

      updateNavTop();
      window.addEventListener("scroll", updateNavTop);

      return () => {
        observer.disconnect();
        window.removeEventListener("scroll", updateNavTop);
        clickHandlers.forEach(({ btn, handler }) => {
          btn.removeEventListener("click", handler);
        });
        if (nav) nav.style.top = "";
      };
    });

    // ── Desktop (>=MD) ──
    mm.add("(min-width: " + MD + "px)", () => {
      function getNavbarH() {
        return navbar ? navbar.offsetHeight : 0;
      }

      function getDistance() {
        return window.innerWidth * (total - 1);
      }

      gsap.set(track, { x: 0 });
      prevIndex = 0;

      const tween = gsap.to(track, {
        x: () => -getDistance(),
        ease: "none",
        scrollTrigger: {
          trigger: wrapper,
          pin: true,
          scrub: 0.3,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          start: () => {
            const h = getNavbarH();
            wrapper.style.setProperty("--navbar-height", h + "px");
            return "top top+=" + h;
          },
          end: () => "+=" + getDistance(),
          onUpdate: (self) => {
            const idx = Math.min(
              Math.floor(self.progress * total),
              total - 1
            );
            if (idx !== prevIndex) {
              animateYear(items[idx].dataset.year || "", self.direction === 1);
              setActiveNav(items[idx].dataset.year || "");
              prevIndex = idx;
            }
          },
        },
      });

      st = tween.scrollTrigger;

      if (st.spacer) {
        st.spacer.style.width = "100%";
        st.spacer.style.maxWidth = "100%";
        st.spacer.style.overflow = "hidden";
      }

      // Nav click handler for desktop
      const clickHandlers = [];
      navButtons.forEach((btn) => {
        const handler = () => {
          const year = btn.dataset.year;
          if (!year || !st) return;
          const targetItem = track.querySelector(
            '.timeline-horizontal-item[data-year="' + year + '"]'
          );
          if (!targetItem) return;
          const index = Array.from(items).indexOf(targetItem);
          if (index === -1) return;
          const targetScroll =
            st.start + (index / (total - 1)) * (st.end - st.start);
          gsap.to(window, {
            scrollTo: targetScroll,
            duration: 0.8,
            ease: "power2.inOut",
          });
        };
        btn.addEventListener("click", handler);
        clickHandlers.push({ btn, handler });
      });

      // Cleanup on breakpoint change
      return () => {
        gsap.set(track, { clearProps: "x" });
        clickHandlers.forEach(({ btn, handler }) => {
          btn.removeEventListener("click", handler);
        });
        st = null;
      };
    });
  });
}
